import React from 'react';
import Slot from '../Common/Slot';
import styles from './SlotTracker.module.css';

export type SlotType = 'HP' | 'ARMOR' | 'HOPE' | 'STRESS' | 'FOCUS';

export interface SlotTrackerProps {
  // Resource configuration
  type: SlotType;
  label: string;
  icon?: string;

  // Slot state
  currentSlots: number;    // How many slots exist (can change dynamically)
  maxSlots: number;        // Maximum possible slots
  markedIndices: number[]; // Which slots are marked

  // Interactions
  onSlotClick: (index: number) => void;
  onClear: () => void;

  // Display options
  showCount?: boolean;     // Show "X/Y" count
  showLabel?: boolean;     // Show label text
  disabled?: boolean;      // Disable all interactions

  // Advanced (optional)
  thresholdMarkers?: number[];  // For HP: show major/severe indicators
  slotVariant?: 'default' | 'compact' | 'large';
  className?: string;
}

/**
 * SlotTracker - Generic reusable component for tracking any resource type
 *
 * Features:
 * - Reuses existing Slot component
 * - Implements mark-up-to-index logic
 * - Inline clear button
 * - Optional count display
 * - Mobile-optimized touch targets
 * - Keyboard navigation support
 * - Accessible labels and ARIA attributes
 *
 * @example
 * <SlotTracker
 *   type="HP"
 *   label="Hit Points"
 *   currentSlots={6}
 *   maxSlots={6}
 *   markedIndices={[0, 1, 2]}
 *   onSlotClick={handleHPClick}
 *   onClear={handleHPClear}
 *   showCount
 * />
 */
export const SlotTracker: React.FC<SlotTrackerProps> = ({
  type,
  label,
  icon,
  currentSlots,
  maxSlots,
  markedIndices,
  onSlotClick,
  onClear,
  showCount = true,
  showLabel = true,
  disabled = false,
  thresholdMarkers = [],
  slotVariant = 'default',
  className = '',
}) => {
  const markedCount = markedIndices.length;
  const hasMarkedSlots = markedCount > 0;

  const containerClasses = [
    styles.slotTracker,
    styles[`slotTracker--${type.toLowerCase()}`],
    styles[`slotTracker--${slotVariant}`],
    disabled && styles['slotTracker--disabled'],
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClasses}>
      {/* Header with label and count */}
      <div className={styles.slotTracker__header}>
        {showLabel && (
          <label className={styles.slotTracker__label}>
            {icon && <span className={styles.slotTracker__icon}>{icon}</span>}
            <span className={styles.slotTracker__labelText}>{label}</span>
          </label>
        )}

        {showCount && (
          <span
            className={styles.slotTracker__count}
            aria-label={`${markedCount} of ${currentSlots} ${label.toLowerCase()} marked`}
          >
            {markedCount}/{currentSlots}
          </span>
        )}
      </div>

      {/* Slots row */}
      <div className={styles.slotTracker__slotsContainer}>
        <div
          className={styles.slotTracker__slots}
          role="group"
          aria-label={`${label} slots`}
        >
          {currentSlots > 0 ? (
            Array.from({ length: currentSlots }, (_, index) => {
              const isMarked = markedIndices.includes(index);
              const isThreshold = thresholdMarkers.includes(index);

              return (
                <div
                  key={`${type}-slot-${index}`}
                  className={styles.slotTracker__slotWrapper}
                >
                  <Slot
                    index={index}
                    marked={isMarked}
                    onClick={() => onSlotClick(index)}
                    type={type}
                    icon={icon}
                    disabled={disabled}
                    className={isThreshold ? styles.slotTracker__thresholdSlot : ''}
                  />
                  {isThreshold && (
                    <div
                      className={styles.slotTracker__thresholdMarker}
                      aria-hidden="true"
                    />
                  )}
                </div>
              );
            })
          ) : (
            <span className={styles.slotTracker__empty}>
              No {label.toLowerCase()} slots
            </span>
          )}
        </div>

        {/* Clear button */}
        {hasMarkedSlots && !disabled && (
          <button
            className={styles.slotTracker__clearButton}
            onClick={onClear}
            type="button"
            aria-label={`Clear all ${label.toLowerCase()}`}
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
};

export default SlotTracker;
